// A Collection groups related experiences together (e.g. "Untold Stories of a
// Geisha", or Chef Jun Okada's culinary sessions). It is its own entity, not
// an experience — the public collection page lists its member experiences
// (see `ExperienceType.collectionId`).
import type { ExperienceRegionType } from './types_experience';

// Firestore collection name.
export const coll_collections = 'collections';

export type CollectionStatusType = 'active' | 'inactive' | 'coming-soon';

export const collectionStatusLabels: Record<CollectionStatusType, string> = {
  active: 'Active',
  inactive: 'Inactive',
  'coming-soon': 'Coming Soon',
};

/**
 * The optional "meet the person behind the collection" block, shown above the
 * experiences list on the public collection page.
 *
 * Ported from the chef introduction on the static `chef-jun-okada.html`, minus
 * that page's three career-milestone tiles (Career Start / Head Chef /
 * Independent) — those dates are already part of the bio text, and a per-person
 * set of hand-kept year tiles does not generalise across collections.
 */
export interface CollectionBioType {
  /** Small eyebrow above the name, e.g. 'Meet the Chef'. */
  eyebrow: string;
  /** Primary name line — the static page used the Japanese spelling, 岡田 隼. */
  name: string;
  /** Secondary name line under it, e.g. the romaji 'Jun Okada'. Optional. */
  secondaryName: string;
  /** Portrait. Blank drops the image column and the bio runs full width. */
  image: string;
  /**
   * HTML, written in the admin's rich-text editor (bold, lists, links) and
   * rendered by `<RichText>`. Plain text with newlines is still accepted and
   * read as one paragraph per line.
   */
  text: string;
  /** Badge over the portrait, e.g. '20+' / 'Years Experience'. Both blank hides it. */
  badgeValue: string;
  badgeLabel: string;
}

export interface CollectionType {
  id: string;
  name: string;
  subtitle: string;
  region: ExperienceRegionType;
  location: string;

  // Heading for the experiences list on the public collection page — 'Exclusive
  // Experiences' over 'Nine carefully curated culinary journeys…' on the static
  // page. Both blank leaves the grid to speak for itself.
  experiencesTitle: string;
  experiencesSubtitle: string;

  heroImage: string;
  status: CollectionStatusType;

  // Undefined (or empty) on collections that have no person to introduce.
  bio?: CollectionBioType;

  // Legacy pointer to the original static HTML page, kept for reference during
  // the migration. Not used for routing (routes are driven by `id`).
  legacyPageUrl?: string;

  // ISO timestamps. Optional until the Firestore backend populates them.
  createdAt?: string;
  updatedAt?: string;
}

// A blank bio, for the admin form to bind to before anything is typed.
export function makeEmptyBio(): CollectionBioType {
  return {
    eyebrow: '',
    name: '',
    secondaryName: '',
    image: '',
    text: '',
    badgeValue: '',
    badgeLabel: '',
  };
}

/**
 * Whether a bio has enough to be worth rendering. An editor may leave the
 * badge or the eyebrow filled from an earlier draft, so those alone don't
 * count — a bio needs a name, some text, or a portrait.
 */
export function hasBio(bio: CollectionBioType | undefined): bio is CollectionBioType {
  return !!bio && [bio.name, bio.text, bio.image].some((value) => value.trim() !== '');
}

// A blank collection for the "new collection" admin form.
export function makeEmptyCollection(): CollectionType {
  return {
    id: '',
    name: '',
    subtitle: '',
    region: 'kanto',
    location: '',
    experiencesTitle: '',
    experiencesSubtitle: '',
    heroImage: '',
    status: 'active',
  };
}
